import React, { Fragment, useMemo, useCallback, useState } from "react"
import { chain, keyBy } from "lodash"
import { Typography, ListItem, IconButton, Button } from "@mui/material"
import { KeyboardArrowUp, KeyboardArrowDown, Close } from "@mui/icons-material"
import OPTooltip from "components/material/tooltip"
import { useModels } from "resources/models"
import { OK } from "helpers/data-statuses"
import { makeHiddenMetric } from "./helpers"

interface HiddenSelectedMetricsProps {
  metrics: Record<string, string[]>
  setSelectedMetrics: React.Dispatch<React.SetStateAction<string[]>>
}

export default function HiddenSelectedMetrics({ metrics, setSelectedMetrics }: HiddenSelectedMetricsProps) {
  const [models, modelsStatus] = useModels()
  const [isCollapsed, setIsCollapsed] = useState<boolean>(false)

  const modelsMap = useMemo(() => {
    if (modelsStatus !== OK || !models) {
      return {}
    }
    return keyBy(models, "id")
  }, [models, modelsStatus])

  const toggleIsCollapsed = useCallback(() => {
    setIsCollapsed(prev => !prev)
  }, [setIsCollapsed])

  const hiddenMetricsMap = useMemo(() =>
    chain(metrics)
      .flatMap((metricList) => metricList)
      .keyBy((metric) => metric)
      .value(),
  [metrics])

  return <div className="metric-ordering-category hidden-metrics">
    <div className="hidden-metrics-header">
      <Typography className="hidden-metrics-label">
        Hidden Metrics
      </Typography>
      <IconButton onClick={toggleIsCollapsed}>
        {isCollapsed
          ? <KeyboardArrowDown />
          : <KeyboardArrowUp />}
      </IconButton>
    </div>
    {!isCollapsed && (
      <div>
        <Typography variant="body2">
          The following metrics have been hidden in the table because they are not available either in this model or this resource. Removing one of these metrics here will remove it from this view for all models/resources.
        </Typography>

        {Object.entries(metrics).map(([modelId, values]) => {
          return <Fragment key={modelId}>
            <ListItem
              className="category-item header"
              data-type="category"
            >
              {modelsMap[modelId]?.name || modelId}
            </ListItem>
            {values.map(item => {
              const hiddenMetric = makeHiddenMetric(item)
              return <ListItem key={item} className="metric-item">
                <div className="metric-item-label">
                  <OPTooltip title={hiddenMetric}>
                    <span className="metric-item-label-text">{hiddenMetric}</span>
                  </OPTooltip>
                </div>
                <IconButton size="small" className="metric-item-action" onClick={() => setSelectedMetrics(metrics => metrics.filter(metric => metric !== item))}>
                  <Close fontSize="small" />
                </IconButton>
              </ListItem>
            })}
          </Fragment>
        })}
        <div className="hidden-metrics-actions">
          <Button
            className="remove-all"
            color="error"
            onClick={() => setSelectedMetrics(metrics => metrics.filter(metric => !hiddenMetricsMap[metric]))}
          >
            Remove All
          </Button>
        </div>
      </div>
    )}
  </div>
}
